/**
 * Cart Drawer - Shopify Theme
 * Maneja la funcionalidad completa del carrito deslizable
 * Compatible con la API de Shopify
 */

class CartDrawer {
  constructor() {
    this.drawer = document.querySelector('cart-drawer');
    this.overlay = document.querySelector('.cart-drawer-overlay');
    this.closeBtn = document.querySelector('.cart-drawer-close');
    this.itemsList = document.querySelector('.cart-drawer-list');
    this.itemsWrapper = document.querySelector('.cart-drawer-items-wrapper');

    // Discount form elements
    this.discountForm = document.querySelector('.cart-drawer-discount-form');
    this.discountInput = document.querySelector('.cart-drawer-discount-input');
    this.discountBtn = document.querySelector('.cart-drawer-discount-btn');
    this.discountMessage = document.querySelector('.cart-drawer-discount-message');

    // Control de estado para evitar abrir/cerrar rápido
    this.isTransitioning = false;
    this.transitionDelay = 350; // ms

    // Control de peticiones simultáneas
    this.isUpdating = false;
    this.updateQueue = [];

    // Control de aplicación de descuento
    this.isApplyingDiscount = false;

    if (!this.drawer) return;

    this.init();
    this.bindEvents();
  }

  init() {
    // Crear botones para abrir el carrito
    this.createCartButtons();

    // Escuchar cambios en el carrito (para AJAX)
    this.setupCartObserver();
  }

  bindEvents() {
    // Cerrar carrito - Solo en overlay
    if (this.overlay) {
      this.overlay.addEventListener('click', (e) => {
        e.stopPropagation();
        this.close();
      });
    }

    if (this.closeBtn) {
      this.closeBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.close();
      });
    }

    // Prevenir que clicks dentro del drawer lo cierren
    const content = document.querySelector('.cart-drawer-content');
    if (content) {
      content.addEventListener('click', (e) => {
        e.stopPropagation();
      });
    }

    // Cerrar con ESC
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.drawer.classList.contains('active')) {
        e.preventDefault();
        this.close();
      }
    });

    // Prevenir scroll cuando el drawer está abierto
    this.drawer.addEventListener('wheel', (e) => {
      if (this.itemsWrapper && !this.isScrollableContainer(e.target)) {
        e.preventDefault();
      }
    }, { passive: false });

    // Discount form submit
    if (this.discountForm) {
      this.discountForm.addEventListener('submit', (e) => {
        e.preventDefault();
        this.applyDiscount();
      });
    }

    // Discount toggle button
    const discountToggle = document.querySelector('.cart-drawer-discount-toggle');
    if (discountToggle && this.discountForm) {
      discountToggle.addEventListener('click', (e) => {
        e.preventDefault();
        this.toggleDiscountForm();
      });
    }

    // Clear message when typing
    if (this.discountInput) {
      this.discountInput.addEventListener('input', () => {
        this.clearDiscountMessage();
      });
    }
  }

  /**
   * Abre el cart drawer
   */
  open() {
    // Validar que no esté en transición y que no esté ya abierto
    if (this.isTransitioning || this.drawer.classList.contains('active')) {
      return;
    }

    this.isTransitioning = true;
    this.drawer.classList.add('active');
    document.body.style.overflow = 'hidden';

    // Ocultar shopify chat
    const shopifyChat = document.getElementById('shopify-chat');
    if (shopifyChat) {
      shopifyChat.style.display = 'none';
    }

    // Cerrar popup added-to-cart si existe
    const addedToCart = document.getElementById('added-to-cart');
    if (addedToCart) {
      // Intentar cerrar si tiene un botón close
      const closeBtn = addedToCart.querySelector('[data-close], .close, button[type="button"]');
      if (closeBtn) {
        closeBtn.click();
      }
      // Si no tiene botón, removemos el elemento
      if (addedToCart.parentElement) {
        addedToCart.remove();
      }
    }

    // Agregar clase al body para ocultar added-to-cart por CSS
    document.body.classList.add('cart-drawer-active');

    // Permitir siguiente acción después de la transición
    setTimeout(() => {
      this.isTransitioning = false;
    }, this.transitionDelay);
  }

  /**
   * Cierra el cart drawer
   */
  close() {
    // Validar que no esté en transición y que esté abierto
    if (this.isTransitioning || !this.drawer.classList.contains('active')) {
      return;
    }

    this.isTransitioning = true;
    this.drawer.classList.remove('active');
    document.body.style.overflow = '';

    // Mostrar shopify chat
    const shopifyChat = document.getElementById('shopify-chat');
    if (shopifyChat) {
      shopifyChat.style.display = '';
    }

    // Remover clase del body para mostrar added-to-cart por CSS
    document.body.classList.remove('cart-drawer-active');

    // Permitir siguiente acción después de la transición
    setTimeout(() => {
      this.isTransitioning = false;
    }, this.transitionDelay);
  }

  /**
   * Alterna la visibilidad del formulario de descuento
   */
  toggleDiscountForm() {
    if (!this.discountForm) return;

    const isHidden = this.discountForm.style.display === 'none';

    if (isHidden) {
      // Mostrar formulario
      this.discountForm.style.display = 'block';
      setTimeout(() => {
        this.discountInput?.focus();
      }, 50);
    } else {
      // Ocultar formulario
      this.discountForm.style.display = 'none';
      this.clearDiscountMessage();
      this.discountInput.value = '';
    }
  }

  /**
   * Verifica si el contenedor es scrolleable
   */
  isScrollableContainer(element) {
    return element === this.itemsWrapper || this.itemsWrapper.contains(element);
  }

  /**
   * Crea los botones para abrir el carrito
   */
  createCartButtons() {
    // Remover listeners anteriores
    document.querySelectorAll('[data-open-cart]').forEach((btn) => {
      // Remover event listeners clonando el elemento
      const newBtn = btn.cloneNode(true);
      btn.parentNode.replaceChild(newBtn, btn);
    });

    // Agregar nuevos listeners
    document.querySelectorAll('[data-open-cart]').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        this.open();
      });
    });
  }

  /**
   * Observa cambios en el carrito y actualiza el drawer
   */
  setupCartObserver() {
    // Escuchar eventos de actualización del carrito (una sola vez en el documento)
    if (!window.cartDrawerListenerAdded) {
      document.addEventListener('cart:updated', () => {
        if (window.cartDrawer) {
          window.cartDrawer.updateDrawer();
        }
      });
      window.cartDrawerListenerAdded = true;
    }

    // Usar event delegation en el contenedor padre en lugar de en itemsList
    const content = document.querySelector('.cart-drawer-content');
    if (content && this.itemsList) {
      // Remover listeners antiguos del itemsList
      const newItemsList = this.itemsList.cloneNode(true);
      this.itemsList.parentNode.replaceChild(newItemsList, this.itemsList);
      this.itemsList = newItemsList;

      // Agregar listeners solo al nuevo itemsList
      this.itemsList.addEventListener('click', (e) => {
        const qtyBtn = e.target.closest('.cart-drawer-qty-btn');
        const removeBtn = e.target.closest('.cart-drawer-item-remove');

        if (qtyBtn) {
          const lineIndex = qtyBtn.dataset.lineIndex;
          const action = qtyBtn.dataset.action;
          const qtyInput = qtyBtn.closest('.cart-drawer-quantity').querySelector('.cart-drawer-qty-input');
          const currentQty = parseInt(qtyInput.value);
          const newQty = action === 'increase' ? currentQty + 1 : Math.max(0, currentQty - 1);

          this.updateQuantity(lineIndex, newQty);
        }

        if (removeBtn) {
          const lineIndex = removeBtn.dataset.lineIndex;
          this.updateQuantity(lineIndex, 0);
        }
      });

      // Escuchar cambios directos en el input de cantidad
      this.itemsList.addEventListener('change', (e) => {
        const qtyInput = e.target.closest('.cart-drawer-qty-input');
        if (qtyInput) {
          const lineIndex = qtyInput.dataset.lineIndex;
          const newQty = parseInt(qtyInput.value) || 0;
          this.updateQuantity(lineIndex, newQty);
        }
      });
    }
  }

  /**
   * Actualiza la cantidad de un item con cola de espera
   */
  async updateQuantity(lineIndex, quantity) {
    // Agregar a la cola si ya hay una actualización en progreso
    if (this.isUpdating) {
      this.updateQueue.push({ lineIndex, quantity });
      return;
    }

    this.isUpdating = true;
    const item = this.itemsList?.querySelector(`li[data-key] .cart-drawer-qty-input[data-line-index="${lineIndex}"]`)?.closest('li');

    if (item) {
      item.classList.add('loading');
    }

    try {
      const response = await fetch(window.Shopify.routes.root + 'cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          line: parseInt(lineIndex),
          quantity: Math.max(0, quantity), // Asegurar que la cantidad no sea negativa
        }),
      });

      if (response.ok) {
        // Solo recargar el drawer, sin disparar eventos
        await this.updateDrawer();
      } else if (response.status === 409) {
        // Error de conflicto - recargar drawer sin hacer más cambios
        console.warn('Conflicto en el carrito, recargando...');
        await this.updateDrawer();
      }
    } catch (error) {
      console.error('Error al actualizar cantidad:', error);
    } finally {
      if (item) {
        item.classList.remove('loading');
      }

      this.isUpdating = false;

      // Procesar siguiente en la cola
      if (this.updateQueue.length > 0) {
        const next = this.updateQueue.shift();
        this.updateQuantity(next.lineIndex, next.quantity);
      }
    }
  }

  /**
   * Aplica el código de descuento al carrito usando iframe
   */
  applyDiscount() {
    if (this.isApplyingDiscount || !this.discountInput) {
      return;
    }

    const code = this.discountInput.value.trim();

    if (!code) {
      this.showDiscountMessage('Por favor ingresa un código de descuento', 'error');
      return;
    }

    this.isApplyingDiscount = true;
    this.setDiscountButtonLoading(true);
    this.clearDiscountMessage();

    console.log('📤 Aplicando descuento:', code);

    this.fetchCart()
        .then((cart) => {
          console.log('📊 Carrito original:', cart);

          const discountUrl = `/discount/${encodeURIComponent(code)}?redirect=/cart`;
          console.log('🔗 URL del descuento:', discountUrl);

          // Crear iframe oculto para aplicar el descuento
          const iframe = document.createElement('iframe');
          iframe.style.display = 'none';
          iframe.src = discountUrl;
          document.body.appendChild(iframe);

          console.log('📦 iframe creado, esperando aplicación...');

          // Esperar a que se procese el descuento
          setTimeout(() => {
            document.body.removeChild(iframe);
            console.log('✅ iframe removido, verificando carrito...');

            // Obtener carrito actualizado
            this.fetchCart()
                .then((updatedCart) => {
                  console.log('📦 Carrito actualizado:', updatedCart);
                  console.log('🔍 Descuentos aplicados:', updatedCart.cart_level_discount_applications);

                  // Verificar si hay descuentos aplicados o si el total cambió
                  const hasDiscounts = updatedCart.cart_level_discount_applications &&
                      updatedCart.cart_level_discount_applications.length > 0;

                  const discountCodes = updatedCart.discount_codes || [];
                  const codeApplied = discountCodes.some(d =>
                      d.code.toUpperCase() === code.toUpperCase()
                  );

                  console.log('📊 hasDiscounts:', hasDiscounts);
                  console.log('📊 codeApplied:', codeApplied);
                  console.log('📊 discount_codes:', discountCodes);

                  if (hasDiscounts || codeApplied) {

                    const discount = updatedCart.cart_level_discount_applications?.[0];
                    console.log('✅ Descuento aplicado correctamente:', discount);

                    this.showDiscountMessage(`Código "${code}" aplicado correctamente`, 'success');
                    this.discountInput.value = '';

                    // Guardar en sessionStorage
                    const sessionStorageValue = {
                      code: code,
                      discountTitle: discount?.title || code,
                    };
                    sessionStorage.setItem('discountCode', JSON.stringify(sessionStorageValue));

                    // Actualizar el drawer
                    this.updateDrawer();

                    // Ocultar formulario después de 2 segundos
                    setTimeout(() => {
                      this.toggleDiscountForm();
                    }, 2000);

                  } else {
                    console.log('❌ Descuento no aplicado - código inválido o no aplicable');
                    console.log('Discount codes en carrito:', discountCodes);
                    this.showDiscountMessage('El código no es válido o no se puede aplicar', 'error');
                  }
                })
                .catch((error) => {
                  console.error('❌ Error verificando carrito:', error);
                  this.showDiscountMessage('Error al aplicar el código. Intenta nuevamente', 'error');
                })
                .finally(() => {
                  this.isApplyingDiscount = false;
                  this.setDiscountButtonLoading(false);
                });
          }, 1500);
        })
        .catch((error) => {
          console.error('❌ Error inicial:', error);
          this.showDiscountMessage('Error al aplicar el código. Intenta nuevamente', 'error');
          this.isApplyingDiscount = false;
          this.setDiscountButtonLoading(false);
        });
  }

  /**
   * Obtiene el carrito actual
   */
  fetchCart() {
    return fetch(window.Shopify.routes.root + 'cart.js', {
      headers: {
        'Content-Type': 'application/json',
      }
    }).then((res) => {
      if (!res.ok) throw new Error('Cart fetch failed');
      return res.json();
    });
  }

  /**
   * Muestra un mensaje de descuento
   */
  showDiscountMessage(message, type) {
    if (!this.discountMessage) return;

    this.discountMessage.textContent = message;
    this.discountMessage.className = `cart-drawer-discount-message ${type}`;

    // Auto-clear error messages after 5 seconds
    if (type === 'error') {
      setTimeout(() => {
        this.clearDiscountMessage();
      }, 5000);
    }
  }

  /**
   * Limpia el mensaje de descuento
   */
  clearDiscountMessage() {
    if (!this.discountMessage) return;

    this.discountMessage.textContent = '';
    this.discountMessage.className = 'cart-drawer-discount-message';
  }

  /**
   * Controla el estado de carga del botón de descuento
   */
  setDiscountButtonLoading(isLoading) {
    if (!this.discountBtn) return;

    if (isLoading) {
      this.discountBtn.disabled = true;
    } else {
      this.discountBtn.disabled = false;
    }
  }

  /**
   * Actualiza el drawer desde el carrito actual
   */
  async updateDrawer() {
    try {
      const response = await fetch(window.Shopify.routes.root + 'cart.js');
      const cart = await response.json();

      // Si el drawer no existe en el DOM, no hacemos nada
      if (!this.drawer) return;

      // Actualizar el contador de productos
      const cartNumberElements = document.querySelectorAll('[data-number-cart]');
      if (cartNumberElements.length > 0) {
        cartNumberElements.forEach(element => {
          element.textContent = cart.item_count;
        });
      }

      // Marcar timestamp de última actualización (para bfcache)
      window._lastCartUpdate = Date.now();

      // Recargar la sección del drawer completa mediante AJAX
      this.refreshDrawerHTML();
    } catch (error) {
      console.error('Error al actualizar drawer:', error);
    }
  }

  /**
   * Recarga el HTML del drawer mediante AJAX
   */
  async refreshDrawerHTML() {
    try {
      const sectionId = this.drawer.dataset.sectionId;
      if (!sectionId) return;

      const response = await fetch(window.Shopify.routes.root + `?sections=${sectionId}`);
      const data = await response.json();
      const newHTML = data[sectionId];

      if (newHTML) {
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = newHTML;
        const newDrawer = tempDiv.querySelector('cart-drawer');

        if (newDrawer) {
          // Preservar el estado "active" del drawer
          const isActive = this.drawer.classList.contains('active');

          // Reemplazar el drawer completo
          this.drawer.replaceWith(newDrawer);

          // Actualizar referencias a los elementos
          this.drawer = newDrawer;
          this.overlay = newDrawer.querySelector('.cart-drawer-overlay');
          this.closeBtn = newDrawer.querySelector('.cart-drawer-close');
          this.itemsList = newDrawer.querySelector('.cart-drawer-list');
          this.itemsWrapper = newDrawer.querySelector('.cart-drawer-items-wrapper');

          // Actualizar referencias al formulario de descuento
          this.discountForm = newDrawer.querySelector('.cart-drawer-discount-form');
          this.discountInput = newDrawer.querySelector('.cart-drawer-discount-input');
          this.discountBtn = newDrawer.querySelector('.cart-drawer-discount-btn');
          this.discountMessage = newDrawer.querySelector('.cart-drawer-discount-message');

          // Re-bindear TODOS los events
          this.bindEvents();
          this.setupCartObserver();
          this.createCartButtons();

          // Mantener el drawer abierto si lo estaba
          if (isActive) {
            this.drawer.classList.add('active');
          }

          // Actualizar referencia global
          window.cartDrawer = this;

          // Reinicializar gift-box si existe
          if (window.GiftBoxManager) {
            setTimeout(() => {
              window.GiftBoxManager.init();
            }, 100);
          }

          // Reinicializar recomendaciones si existe
          if (window.CartRecommendationsManager) {
            setTimeout(() => {
              window.CartRecommendationsManager.init();
            }, 150);
          }
        }
      }
    } catch (error) {
      console.error('Error al recargar drawer HTML:', error);
    }
  }
}

// Inicializar cuando el DOM esté listo
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.cartDrawer = new CartDrawer();
  });
} else {
  window.cartDrawer = new CartDrawer();
}

// Exponer métodos globales
window.CartDrawer = {
  open: () => window.cartDrawer?.open(),
  close: () => window.cartDrawer?.close(),
  toggle: () => window.cartDrawer?.toggle(),
  applyDiscount: (code) => {
    if (window.cartDrawer && code) {
      window.cartDrawer.discountInput.value = code;
      window.cartDrawer.applyDiscount();
    }
  },
};

/**
 * Integración con Form de añadir al carrito
 * Si tu tema usa el custom element product-form
 */
document.addEventListener('product:added-to-cart', () => {
  if (window.cartDrawer) {
    window.cartDrawer.open();
  }
});

/**
 * Integración con Ajax Add to Cart estándar
 * Intercepta fetch y XMLHttpRequest a /cart/add.js para abrir el drawer
 * SIN duplicar productos - solo detecta y abre
 */
(function() {
  // Interceptar FETCH
  const originalFetch = window.fetch;
  window.fetch = function(...args) {
    const url = args[0];
    // Lanzamos el fetch original y devolvemos SU promesa intacta, para no
    // alterar el manejo de errores de quien llama (apps de terceros, beacons, etc.).
    const fetchPromise = originalFetch.apply(this, args);

    // Observador independiente: solo mira el resultado y NUNCA propaga errores,
    // evitando el "Uncaught (in promise): Failed to fetch" en peticiones ajenas.
    fetchPromise
        .then(response => {
          const u = typeof url === 'string' ? url : (url && url.url) || '';
          // Solo interceptar cart/add.js exitosos
          if (u.includes('cart/add') && response && response.ok) {
            response.clone().json().then(() => {
              setTimeout(() => {
                document.dispatchEvent(new CustomEvent('cart:updated'));
                if (window.cartDrawer) {
                  window.cartDrawer.open();
                }
              }, 100);
            }).catch(() => {});
          }
        })
        .catch(() => {});

    return fetchPromise;
  };

  // Interceptar XMLHttpRequest (para temas que usan AJAX antiguo)
  const originalXHROpen = XMLHttpRequest.prototype.open;
  const originalXHRSend = XMLHttpRequest.prototype.send;

  XMLHttpRequest.prototype.open = function(method, url, ...rest) {
    this._cartDrawerUrl = url;
    this._cartDrawerMethod = method;
    return originalXHROpen.apply(this, [method, url, ...rest]);
  };

  XMLHttpRequest.prototype.send = function(...args) {
    // Solo interceptar POST a cart/add
    if (this._cartDrawerUrl &&
        this._cartDrawerUrl.includes('cart/add') &&
        this._cartDrawerMethod?.toUpperCase() === 'POST') {
      this.addEventListener('load', function() {
        if (this.status >= 200 && this.status < 300) {
          setTimeout(() => {
            document.dispatchEvent(new CustomEvent('cart:updated'));
            if (window.cartDrawer) {
              window.cartDrawer.open();
            }
          }, 100);
        }
      });
    }
    return originalXHRSend.apply(this, args);
  };

  // ----------------------------------------------------------------------
  // Silencia SOLO el ruido de consola provocado por peticiones de RED de
  // terceros que fallan y que esos scripts no capturan (típicamente los web
  // pixels / eventos de Shopify: net::ERR_FAILED -> 'TypeError: Failed to fetch').
  //
  // IMPORTANTE: esto NO afecta a los .catch ya existentes (siguen ejecutándose).
  // Solo evita que un rechazo de red SIN manejar ensucie la consola. No oculta
  // otros tipos de error (sintaxis, referencias, rechazos no-red, etc.).
  // Si prefieres ver TODO en consola, elimina este bloque.
  window.addEventListener('unhandledrejection', function (event) {
    var reason = event && event.reason;
    var msg = (reason && reason.message) ? String(reason.message) : String(reason || '');
    if (/Failed to fetch|Load failed|NetworkError|ERR_FAILED/i.test(msg)) {
      event.preventDefault();
    }
  });
})();

/**
 * ============================================================
 * BFCache Fix - Solución para navegación hacia atrás
 * ============================================================
 *
 * Problema: Cuando el usuario presiona "atrás" en el navegador,
 * este restaura la página desde bfcache (back-forward cache) sin
 * ejecutar DOMContentLoaded, mostrando datos obsoletos del carrito.
 */

// Detectar navegación desde bfcache y actualizar carrito
window.addEventListener('pageshow', (event) => {
  // event.persisted = true significa que la página viene del bfcache
  if (event.persisted) {
    console.log('🔄 Página restaurada desde bfcache, actualizando carrito...');

    // Actualizar el contador y drawer del carrito
    fetch(window.Shopify.routes.root + 'cart.js', {
      headers: { 'Content-Type': 'application/json' }
    })
        .then(res => res.json())
        .then(cart => {
          // Actualizar todos los contadores del carrito
          document.querySelectorAll('[data-number-cart]').forEach(el => {
            el.textContent = cart.item_count;
          });

          // Si el cart drawer existe, actualizarlo completamente
          if (window.cartDrawer) {
            window.cartDrawer.updateDrawer();
          }

          console.log('✅ Carrito actualizado desde bfcache:', cart.item_count, 'items');
        })
        .catch(err => {
          console.error('Error actualizando carrito desde bfcache:', err);
        });
  }
});

// También escuchar el evento 'visibilitychange' para cuando el usuario
// vuelve a la pestaña después de estar en otra
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'visible') {
    // Solo actualizar si pasó algo de tiempo (evitar actualizaciones innecesarias)
    const lastUpdate = window._lastCartUpdate || 0;
    const now = Date.now();

    // Si pasaron más de 30 segundos desde la última actualización
    if (now - lastUpdate > 30000) {
      console.log('🔄 Pestaña visible, verificando carrito...');

      fetch(window.Shopify.routes.root + 'cart.js', {
        headers: { 'Content-Type': 'application/json' }
      })
          .then(res => res.json())
          .then(cart => {
            document.querySelectorAll('[data-number-cart]').forEach(el => {
              el.textContent = cart.item_count;
            });

            // Si el drawer existe y está abierto, actualizarlo
            if (window.cartDrawer && window.cartDrawer.drawer?.classList.contains('active')) {
              window.cartDrawer.updateDrawer();
            }

            window._lastCartUpdate = now;
          })
          .catch(() => {});
    }
  }
});